import { Button } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/auth/useAuth';
import { useGetTransportPaymentPricePlan } from '../../hooks/payments/useGetTransportPaymentPricePlan';
import { useSendTransportParameters } from '../../hooks/payments/useSendTransportParameters';
import { useGetAuthenticatedParentProfile } from '../../hooks/profile/parent/useGetAuthenticatedParentProfile';
import type { BoricaParametersDto } from '../../models/payments/borica-parameters.dto';
import type { TransportParametersDto } from '../../models/payments/transport-parameters.dto';
import type { TransportPricePlanDto } from '../../models/transport/payments/transport-price.plan.dto';
import { MONTH_NAMES } from '../../utils/constants/dateContstants';
import { DEFAULT_PAYMENT_FORM_STATE } from '../../utils/constants/payments/default-payment-form-state';
import { displayPrice } from '../../utils/displayPrice';
import { ScopeAccess } from '../../utils/enums/scope-access.enum';
import { FormSkeleton } from '../shared/components/skeletons/FormSkeleton';
import { BoricaResponseForm } from './BoricaResponseForm';

export function TransportPayment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get('month') ?? '';
  const yearParam = searchParams.get('year') ?? '';
  const studentId = searchParams.get('studentId') ?? '';
  const withFallback = searchParams.get('withFallback') ?? '';
  const { authState } = useAuth();

  const [formState, setFormState] = useState<BoricaParametersDto>(
    DEFAULT_PAYMENT_FORM_STATE
  );

  const { data: parentProfile } = useGetAuthenticatedParentProfile();
  const mainParent = parentProfile?.parents.find(
    (parent) => parent.isPrimaryParent
  );
  const { data: transportPaymentPricePlan, isPending } =
    useGetTransportPaymentPricePlan(monthParam, yearParam);

  const sendTransportParameters = useSendTransportParameters();
  const sendTransportParametersPending =
    sendTransportParameters.state?.[0]?.status === 'pending';

  const methods = useForm<{ studentId: string }>({
    defaultValues: {
      studentId: '',
    },
  });

  const onNavigateBack = () => {
    if (Boolean(withFallback)) {
      navigate(-1);
    } else {
      navigate('/dashboard/payments');
    }
  };

  useEffect(() => {
    if (formRef.current && formState.saleRequest.AMOUNT) {
      formRef.current?.submit();
    }
  }, [formState]);

  const onPayNow = async (data: { studentId: string }) => {
    const formData: TransportParametersDto = {
      ...data,
      month: +monthParam,
      year: +yearParam,
    };
    const res = await sendTransportParameters.mutation.mutateAsync(formData);
    setFormState(res);
  };

  const handlePayPerStudent = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const payButton = event.target as HTMLButtonElement;

    methods.handleSubmit(() => {
      onPayNow({
        studentId: payButton.id,
      });
    })();
  };

  const renderStudents = () => {
    let studentsPlans = transportPaymentPricePlan?.pricePlans;

    if (
      studentId &&
      monthParam &&
      yearParam &&
      transportPaymentPricePlan?.pricePlans
    ) {
      queryClient.invalidateQueries({ queryKey: ['transportation-taxes'] });
      studentsPlans = transportPaymentPricePlan?.pricePlans.filter(
        (studentPlan) => studentPlan.student.id === studentId
      );
    }

    if (!studentsPlans?.length && !isPending) {
      return (
        <div
          className="payments__pre-payment__student-section"
          style={{ textAlign: 'center', padding: '2rem' }}
        >
          <p className="payments__pre-payment__value">
            {t('PAYMENTS.TITLES.NO_UNPAID_PLANS')}
          </p>
        </div>
      );
    }

    return (
      studentsPlans &&
      studentsPlans.map((paymentPlan: TransportPricePlanDto, index: number) => (
        <div key={index}>
          <div className="payments__pre-payment__student-section">
            <div className="payments__pre-payment__two-column-layout">
              <div className="payments__pre-payment__student-info">
                <p className="payments__pre-payment__label">
                  {t('USERS.TABLE.ROLE.STUDENT')}
                </p>
                <p className="payments__pre-payment__value">
                  {paymentPlan.student.name}
                </p>
              </div>
              <div className="payments__pre-payment__student-info payments__pre-payment__student-info--divider">
                <p className="payments__pre-payment__label">
                  {t('USERS.TABLE.ROLE.PARENT')}
                </p>
                <p className="payments__pre-payment__value">
                  {mainParent?.name}
                </p>
              </div>
            </div>
          </div>

          <h4 className="payments__pre-payment__section-title">
            {t('PAYMENTS.TITLES.TRANSPORTATION_PLANS')}{' '}
            {t(`MONTHS.${MONTH_NAMES[+monthParam]}`)}
          </h4>

          <div className="payments__pre-payment__installment">
            <div className="payments__pre-payment__installment-grid payments__pre-payment__installment-grid--transport">
              <div className="payments__pre-payment__installment-item">
                <p className="payments__pre-payment__label">
                  {t('TAXES.TABLE.HEADERS.SCHOOL_YEAR')}
                </p>
                <p className="payments__pre-payment__value">
                  {yearParam} - {Number(yearParam) + 1}
                </p>
              </div>
              <div className="payments__pre-payment__installment-item payments__pre-payment__installment-item--amount">
                <p className="payments__pre-payment__label">
                  {t('TAXES.TABLE.HEADERS.AMOUNT')}
                </p>
                <p className="payments__pre-payment__value">
                  {displayPrice(
                    paymentPlan.amount,
                    t('PAYMENTS.LABELS.BGN'),
                    t('PAYMENTS.LABELS.EUR'),
                    paymentPlan.currency
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="payments__pre-payment__total-section">
            <div className="payments__pre-payment__total-row">
              <span className="payments__pre-payment__total-label">
                {t('PAYMENTS.TITLES.TOTAL')}
              </span>
              <span className="payments__pre-payment__total-value">
                {displayPrice(
                  paymentPlan.amount,
                  t('PAYMENTS.LABELS.BGN'),
                  t('PAYMENTS.LABELS.EUR'),
                  paymentPlan.currency
                )}
              </span>
            </div>
          </div>

          {authState?.scope?.includes(ScopeAccess.MakePayments) && (
            <div className="payments__pre-payment__actions">
              <Button variant="outlined" onClick={onNavigateBack}>
                {t('ACTIONS.BACK')}
              </Button>
              <Button
                type="submit"
                id={paymentPlan.student.id}
                onClick={handlePayPerStudent}
                loading={sendTransportParametersPending}
              >
                {t('ACTIONS.PAY_NOW')}
              </Button>
            </div>
          )}
        </div>
      ))
    );
  };

  return (
    <div className="payments__pre-payment">
      {isPending && <FormSkeleton />}
      {!isPending && (
        <FormProvider {...methods}>
          <form>
            <h3 className="payments__pre-payment__title">
              {t('PAYMENTS.TITLES.TRANSPORT_ORDER')}
            </h3>
            {renderStudents()}
          </form>
        </FormProvider>
      )}
      <BoricaResponseForm ref={formRef} formState={formState} />
    </div>
  );
}
